import React, { useRef, useCallback, useState, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Button as MuiButton } from '@mui/material';
import moment from 'moment';
import useStyles from './signature-pad.styles';
import clsx from 'clsx';

const SignaturePad = ({ value, onChange, error }) => {
  const classes = useStyles();
  const sigRef = useRef(null);
  const wrapperRef = useRef(null);
  const [hasStrokes, setHasStrokes] = useState(false);

  // Watch for container resize (e.g. scrollbar appearing after tall images are added)
  // and re-sync the canvas internal resolution with its CSS display size.
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper || !sigRef.current) return;

    const observer = new ResizeObserver(() => {
      const canvas = sigRef.current?.getCanvas();
      if (!canvas) return;
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      const displayW = canvas.offsetWidth;
      const displayH = canvas.offsetHeight;
      // Only resize if the internal resolution is stale
      if (canvas.width !== displayW * ratio || canvas.height !== displayH * ratio) {
        // Preserve existing strokes across the resize
        const ctx = canvas.getContext('2d');
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        canvas.width = displayW * ratio;
        canvas.height = displayH * ratio;
        ctx.scale(ratio, ratio);
        ctx.putImageData(imageData, 0, 0);
      }
    });

    observer.observe(wrapper);
    return () => observer.disconnect();
  }, [value]);

  const handleEnd = useCallback(() => {
    if (!sigRef.current || sigRef.current.isEmpty()) return;
    setHasStrokes(true);
  }, []);

  const handleSave = useCallback(() => {
    if (!sigRef.current || sigRef.current.isEmpty()) return;
    const dataUrl = sigRef.current.getCanvas().toDataURL('image/png');
    onChange({
      data: dataUrl,
      timestamp: new Date().toISOString(),
    });
  }, [onChange]);

  const handleClear = useCallback(() => {
    if (sigRef.current) {
      sigRef.current.clear();
    }
    setHasStrokes(false);
    onChange(null);
  }, [onChange]);

  const hasSigned = value && value.data;

  return (
    <div className={clsx(classes.container, error && classes.error)}>
      <div className={classes.label}>{`* Sign below`}</div>
      {!hasSigned ? (
        <div ref={wrapperRef} className={classes.canvasWrapper}>
          <SignatureCanvas
            ref={sigRef}
            penColor="black"
            canvasProps={{ className: classes.canvas }}
            onEnd={handleEnd}
          />
        </div>
      ) : (
        <div className={classes.preview}>
          <img src={value.data} alt="Signature" className={classes.previewImage} />
          <span className={classes.timestamp}>
            {`Signed ${moment(value.timestamp).format('L hh:mma')}`}
          </span>
        </div>
      )}
      <div className={classes.actions}>
        <MuiButton size="small" onClick={handleClear} disabled={!hasSigned && !hasStrokes}>
          Clear
        </MuiButton>
        {!hasSigned && (
          <MuiButton size="small" variant="contained" color="primary" onClick={handleSave} disabled={!hasStrokes} className={classes.saveButton}>
            Save
          </MuiButton>
        )}
      </div>
    </div>
  );
};

export default SignaturePad;
